import type { ZoneId, ChatOutMsg, SystemMsg } from "@greg-chick/shared";
import type { WorldObject } from "./types";

export type NetStatus = "single" | "connecting" | "online";

// Phaser ↔ React 통신용 경량 타입 이벤트 버스. 한 모듈 장애 격리(FR-COM-01).
export interface WorldEvents {
  object: WorldObject; // 인터랙션 오브젝트 트리거 → React 팝업
  zone: { zone: ZoneId; title: string }; // 존 전환 알림 → HUD
  ready: { zone: ZoneId };
  /**
   * 🔴 **이 구역 맵을 못 받았다.** 화면이 「고장」과 「빈 구역」을 가르려면 필요하다.
   * **상태**라 STICKY 에 든다 — 화면(`WorldOverlay`)은 `ready` 를 보고 **그제서야**
   * 생기므로, 다시 안 주면 첫 입장을 통째로 놓친다(`zone` 에서 겪은 그 일).
   */
  mapOffline: { zone: ZoneId; offline: boolean };
  /**
   * ⏳ **지금 그림을 얼마나 받았나** — 부팅 화면의 진행 표시(조치계획서 ■2).
   *
   * **상태**라 STICKY 에 든다 — 부팅 화면은 월드보다 먼저 떠 있지만, 늦게 듣기 시작하는
   * 경우(다시 그려질 때)에도 마지막 값을 받아야 막대가 0 으로 되돌아가지 않는다.
   * 한 묶음 기준이다(여러 묶음을 이어 받는다) — 정확한 총량이 아니라 「돌아가고 있다」는 신호다.
   */
  loading: { done: number; total: number };
  // ── M2 멀티 ──
  chat: ChatOutMsg; // 서버 채팅 수신 → 채팅 UI
  system: SystemMsg; // 진행자 공지/음소거/강퇴 → 알림
  netStatus: NetStatus; // 연결 상태 → HUD 표시
  fx: { low: boolean }; // 그래픽 품질 토글(저사양) → Phaser 적용
  // 설정 시트(랜딩·월드 공용)의 소리 토글 → 월드 사운드가 실제로 켜지고 꺼지도록.
  // 저장(soundPref)만 해서는 **이미 재생 중인 앰비언스가 멈추지 않는다**.
  sound: { on: boolean };
  stars: { zone: ZoneId; got: number; total: number; justGot?: boolean }; // 별 모으기 수집 → HUD/칭찬
  travel: { zone: ZoneId }; // 하단 바로가기 바 → Phaser 존 이동(목업 하단 메뉴)
}

type Handler<T> = (payload: T) => void;

/**
 * 🔴 **늦게 듣는 쪽에게 마지막 값을 다시 준다 — 상태형만.**
 *
 * 실제로 이런 일이 났다: 체험공간에 들어오면 `WorldScene` 이 `zone` 을 알리고 **바로 다음
 * 줄에** `ready` 를 알린다. 그런데 화면(`WorldOverlay`)은 `ready` 를 보고 **그제서야
 * 생긴다** — 그러니 `zone` 은 아무도 안 듣는 채로 지나간다. 결과는 두 가지였다:
 *  · 상단 이름이 어느 구역에 가도 「그림책 마을」(기본값) — 도서관에서도 경주에서도
 *  · 운영 집계 `zone_enter` 가 **첫 입장을 통째로 놓친다**(운영 기록: 8/31 입장 9 · 구역 0)
 * 아무 에러도 안 났고, 걸어서 구역을 옮기면 그때부터는 맞게 나와서 더 안 보였다.
 *
 * **행동**(누름·채팅·이동 요청)은 다시 주면 안 된다 — 지나간 일이 또 일어난 것이 된다.
 * 여기 든 것은 「지금 어떤 상태인가」뿐이다.
 */
const STICKY: ReadonlySet<keyof WorldEvents> = new Set([
  "zone",
  "netStatus",
  "stars",
  "mapOffline",
  "loading",
]);

class WorldBus {
  private map = new Map<keyof WorldEvents, Set<Handler<unknown>>>();
  /** 상태형 이벤트의 마지막 값. 늦게 듣는 쪽이 바로 받는다. */
  private last = new Map<keyof WorldEvents, unknown>();

  on<K extends keyof WorldEvents>(
    type: K,
    handler: Handler<WorldEvents[K]>,
  ): () => void {
    const set = this.map.get(type) ?? new Set();
    set.add(handler as Handler<unknown>);
    this.map.set(type, set);
    if (STICKY.has(type) && this.last.has(type)) {
      handler(this.last.get(type) as WorldEvents[K]);
    }
    return () => set.delete(handler as Handler<unknown>);
  }

  emit<K extends keyof WorldEvents>(type: K, payload: WorldEvents[K]): void {
    if (STICKY.has(type)) this.last.set(type, payload);
    this.map.get(type)?.forEach((h) => (h as Handler<WorldEvents[K]>)(payload));
  }

  /**
   * 체험공간을 떠날 때 기억을 지운다 — 안 지우면 **다음에 들어올 때 옛 구역**이 먼저
   * 튀어나온다(버스는 앱 전체가 하나를 같이 쓴다).
   */
  forget(): void {
    this.last.clear();
  }

  clear(): void {
    this.map.clear();
    this.last.clear();
  }
}

export const worldBus = new WorldBus();

// ── 입력(조이스틱·점프) 공유 상태 — React 가 쓰고 Phaser 가 읽음 ──
export const moveInput = { vx: 0, vy: 0, jump: false };
export function setMoveInput(vx: number, vy: number): void {
  moveInput.vx = vx;
  moveInput.vy = vy;
}
/** 모바일 점프 버튼 → Phaser 가 다음 프레임에 소비(엣지 트리거). */
export function requestJump(): void {
  moveInput.jump = true;
}
